import axios from 'axios'
import type { AxiosResponse, AxiosError, AxiosRequestConfig, Method } from 'axios'
import authHeader from '../services/auth-header'

/**
 * Helper function for building API endpoints.
 */
export default function apiEndpoint(options: ApiEndpointOptions, signal?: AbortSignal): Promise<any> {

  const defaultOptions = {
    method: 'get',
    payload: null,
    responseHandler: function (response: AxiosResponse) {
      return response.data
    },
    errorHandler: function (error: AxiosError) {
      // https://axios-http.com/docs/handling_errors
      if (axios.isCancel(error)) {
        // just rethrow the error, so the caller can detect that the request was canceled
        // (there is almost certainly a better way to do this)
        throw error
      }
      if (error.response) {
        // The HTTP request completed, but the server responded with a non-2xx
        // status
        throw error.response.data
      } else if (error.request) {
        // The request was made but no response was received
        throw 'Unknown error'
      } else {
        throw 'Unknown error'
      }
    },
  }

  const effectiveOptions = Object.assign(defaultOptions, options)

  /**
   * Validate options
   */

  if (!('url' in effectiveOptions)) {
    throw 'The "url" option is required'
  }

  const methodDefinitions = {
    get: { takesPayload: false },
    post: { takesPayload: true },
    put: { takesPayload: true },
    delete: { takesPayload: false },
  }

  if (!(effectiveOptions.method in methodDefinitions)) {
    throw 'Invalid or unsupported HTTP method: "' + effectiveOptions.method
  }

  /**
   * Build and return the promise
   */

  const axiosOptions: AxiosRequestConfig = {
    url: effectiveOptions.url,
    method: effectiveOptions.method,
    headers: authHeader(),
    signal: signal,
    data: effectiveOptions.payload,
  }

  return axios.request(axiosOptions)
    .then(effectiveOptions.responseHandler)
    .catch(effectiveOptions.errorHandler)

}

type ApiEndpointOptions = {
  url: string
  method: Method
  payload?: any
  responseHandler?: ResponseHandler
  errorHandler?: ErrorHandler
}

type ResponseHandler = (response: AxiosResponse) => any

type ErrorHandler = (error: AxiosError) => any
